import InterceptorName from '../interceptor/InterceptorName';
import { DeliveryMode, PlayType, SourceType } from '../common/constants';
import { Ecdn11Block } from '../reporter/report/Report';
import { EventReport } from '../common/Reporter/events/interfaces';
import SDKConfig from '../common/SDKConfig';
import { ReportingState, TrackInfo } from '../reporter/constants';
import { GetIceCandidatesFunction } from '@kollective-frontend/ksdk-api';
import { localData, StoredData } from '../util/storedData';

export type SetIntervalType = ReturnType<typeof setInterval>;
// Everything is optional as this is for a constructor or for events which can
// have partial instances of the state.
export interface MaybeLocalState {
  reporter?: MaybeReporterState;
  play?: MaybePlayState;
  event?: EventState;
}

// Everything is optional as this is for a constructor.
export interface MaybePlayState {
  activeMode?: DeliveryMode;
  contentHost?: string;
  contentId?: string;
  getIceCandidates?: GetIceCandidatesFunction;
  // Flag to set when the plugin is disabled, because of an error or because
  // peering is disabled to begin with.
  isKollectiveDisabled?: boolean;
  playType?: PlayType;
  playTypeOverridden?: boolean;
  sdkConfig?: SDKConfig;
  sdnPlaybackFailed?: boolean;
  sourceType?: SourceType;
  stopped?: boolean;
  tenantId?: string;
}

export interface AgentDownloadResponse {
  cacheHitBytes: number;
  ingestBytes: number;
  peerBytes: number;
}

export interface MaybeReporterState {
  activeClusterId?: string;
  agentClusterId?: string;
  agentTransferBytes?: AgentDownloadResponse;
  contentMoid?: string;
  interceptors?: InterceptorName[];
  b?: number; // bitrate
  browserUserGuid?: string;
  contentToken?: string;
  duration?: number;
  ecdn11?: Ecdn11Block;
  ecdn11LastCheck?: number;
  finalSeekingEvent?: EventReport;
  hasReporterInitialized?: boolean;
  hasReporterTimedOut?: boolean;
  ignoreDurationChange?: boolean;
  // SRV-5527: HACK to support moving off of TLE
  isMicrosoftTeamsTownHall?: boolean;
  isReportingDisabled?: boolean;
  latestEvent?: EventReport;
  nodeId?: string;
  novelData?: boolean;
  oldActiveClusterId?: string;
  oldAgentClusterId?: string;
  reportIndex?: number;
  reportingState?: ReportingState;
  reportIntervalId?: ReturnType<typeof setInterval> | null;
  reportSetTimeoutId?: ReturnType<typeof setTimeout>;
  reportStatus?: number;
  reporterStartable?: boolean; // has seen play type event.
  hasSeenDuration?: boolean; // replaces reporterStartable ---
  hasRequiredData?: boolean; // replaces reporterStartable / ...
  initialReportDataSent?: boolean; // replaces requiresInitialReportData...
  sessionToken?: string;
  startSetIntervalId?: SetIntervalType;
  supportsPublish?: boolean;
  tags?: string[]; // locality tags
  title?: string;
  user?: string;
  userEmail?: string;
}

export const toReporterState = (state?: MaybeReporterState): ReporterState => {
  return {
    interceptors: [],
    reportIndex: 0,
    reportingState: ReportingState.WAITING,
    ...(state || {})
  };
};

export const toEventState = (state?: EventState): EventState => {
  state = state || {};
  return state;
};
export interface EventState {
  duration?: number;
  hasReceivedContentResponse?: boolean;
  mute?: boolean;
  at?: TrackInfo;
  ct?: TrackInfo;
  vr?: string; // Video Resolution
  cr?: string; // Client Resolution
}

export default class LocalState {
  play: PlayState;
  reporter: ReporterState;
  event: EventState;

  constructor(state?: MaybeLocalState) {
    this.play = new PlayState(state?.play);
    this.event = toEventState(state && state.event);
    this.reporter = toReporterState(state && state.reporter);
  }
}

export class PlayState implements MaybePlayState {
  private _data?: StoredData;
  activeMode?: DeliveryMode;
  contentHost?: string;
  contentId?: string;
  getIceCandidates?: GetIceCandidatesFunction;
  isKollectiveDisabled?: boolean;
  playType: PlayType;
  playTypeOverridden?: boolean;
  sdkConfig: SDKConfig;
  sdnPlaybackFailed: boolean;
  sourceType?: SourceType;
  stopped?: boolean;
  tenantId?: string;

  constructor(state?: MaybePlayState) {
    this.activeMode = state?.activeMode;
    this.contentHost = state?.contentHost;
    this.contentId = state?.contentId;
    this.getIceCandidates = state?.getIceCandidates;
    this.isKollectiveDisabled = state?.isKollectiveDisabled;
    this.playType = state?.playType ?? PlayType.UNKNOWN;
    this.playTypeOverridden = state?.playTypeOverridden;
    this.sdkConfig = state?.sdkConfig ?? new SDKConfig();
    this.sdnPlaybackFailed = state?.sdnPlaybackFailed ?? false;
    this.sourceType = state?.sourceType;
    this.stopped = state?.stopped;
    this.tenantId = state?.tenantId;
  }

  get playGuid(): string {
    return this.getStoredData().playGuid;
  }

  get start(): number {
    return this.getStoredData().start;
  }

  private getStoredData(reset?: boolean): StoredData {
    if (!reset && this._data) {
      return this._data;
    }
    this._data = localData(reset ?? false, this.contentId);
    return this._data;
  }
}

export interface ReporterState extends MaybeReporterState {
  interceptors: InterceptorName[];
  reportIndex: number;
  reportingState: ReportingState;
}

type LocalStateRecord = Record<string, Record<string, unknown>>;

export const updateLocalState = (
  original: LocalState,
  change: MaybeLocalState
): void => {
  Object.entries(change).forEach(([item, value]) => {
    const o = original as unknown as LocalStateRecord;
    Object.entries(value).forEach(([i, v]) => {
      o[item][i] = v;
    });
  });
};
